import { useEffect, useRef } from 'react';
import {
  getViewportForBounds,
  useReactFlow,
  useStoreApi,
  type Edge,
  type Node,
  type Rect,
  type ViewportHelperFunctionOptions,
  type XYPosition,
} from '@xyflow/react';
import { MOVE_EASE } from './motion';
import type { PositionMover } from './useAnimatedPositions';
import { anchoredViewport, centredOn, ringOneIds, targetBounds, type FlowNodeData } from './useGraphLayout';

/**
 * A one-shot request to move the view, consumed once by identity (a new object each time, so asking twice
 * moves twice); a later request replaces one not yet handled.
 * - `centre`: put the node at the centre of the view and show every node an edge joins it to (its ring 1 when
 *   it is the layout centre), zooming out as far as that needs, or in up to zoom 1.
 * - `anchor`: keep a node where it is on screen, at the same zoom, through the layout change that comes with
 *   it (an expansion or a collapse). The first of `nodeIds` laid out both before and after is the anchor.
 * - `fit`: fit these nodes in the view.
 */
export type ViewportRequest =
  { kind: 'centre'; nodeId: string } | { kind: 'anchor'; nodeIds: string[] } | { kind: 'fit'; nodeIds: string[] };

/** A view move that keeps pace with the nodes' move to a new layout, which takes `moveMs` (see `moveDuration`). */
const inStepWithNodes = (moveMs: number) => ({ duration: moveMs, ease: MOVE_EASE, interpolate: 'linear' as const });

type ReframeProps = {
  layoutKey: string;
  positions: ReadonlyMap<string, XYPosition>;
  /** The nodes at their places in the layout. Every fit reads these, not the nodes on their way there. */
  targets: Node<FlowNodeData>[];
  edges: Edge[];
  /** How long the nodes' move to this layout takes. */
  moveMs: number;
  /** Where the nodes are drawn, and where they were when this layout came. */
  mover: PositionMover;
  request: ViewportRequest | null;
};

/**
 * Moves the view when a new layout lands or a new request comes. A new request is handled once:
 * - `centre` puts its node at the centre of the view and fits every node an edge joins it to, zooming out as
 *   far as that needs, or in up to zoom 1 (the cards' natural size);
 * - `anchor` keeps its node on the same screen point at the same zoom, so the rest of the graph grows or shrinks
 *   around it;
 * - `fit` fits its nodes, at zoom 1 at most.
 * A new layout with no new request (a focus or filter change, a refetch), or a request whose node is not laid
 * out, fits the whole view. Fits read the nodes' places in the new layout, and every view move runs in step with
 * the nodes' move, so the view and the nodes arrive together. Every move is instant when the nodes' is.
 */
export function Reframe({ layoutKey, positions, targets, edges, moveMs, mover, request }: ReframeProps) {
  const { getInternalNode, getViewport, setViewport } = useReactFlow();
  const store = useStoreApi();
  const handled = useRef<ViewportRequest | null>(null);
  const shownLayout = useRef(layoutKey);
  useEffect(() => {
    // Where the anchor node was on screen: where it was drawn when this layout came, part-way through an earlier
    // move or not; with no new layout, where it is drawn now.
    const before = layoutKey === shownLayout.current ? mover.onScreen() : mover.before();
    shownLayout.current = layoutKey;
    const fresh = request !== null && request !== handled.current ? request : null;
    handled.current = request;
    // Fits `bounds`; `false` when there is none.
    const fitBox = (bounds: Rect | null, maxZoom: number, options: ViewportHelperFunctionOptions) => {
      if (!bounds) return false;
      const { width, height, minZoom } = store.getState();
      void setViewport(getViewportForBounds(bounds, width, height, minZoom, maxZoom, 0.2), options);
      return true;
    };
    // Fits the layout places of `nodeIds` (every node when `null`); `false` when none is laid out.
    const fit = (nodeIds: readonly string[] | null, maxZoom: number, options: ViewportHelperFunctionOptions) =>
      fitBox(targetBounds(targets, nodeIds), maxZoom, options);
    // The node's ring-1 box, grown so the node's card is its centre: the card's measured size once React Flow
    // has one (as Centre on focus reads it), else its layout slot. `null` when the node is not laid out.
    const ringOneBox = (nodeId: string) => {
      const card = targets.find((n) => n.id === nodeId);
      const box = targetBounds(targets, ringOneIds(nodeId, edges));
      if (!card || !box) return null;
      const size = getInternalNode(nodeId)?.measured;
      const width = size?.width ?? card.initialWidth ?? 0;
      const height = size?.height ?? card.initialHeight ?? 0;
      return centredOn(box, { x: card.position.x + width / 2, y: card.position.y + height / 2 });
    };
    const inStep = inStepWithNodes(moveMs);
    const reframe = () => {
      if (fresh?.kind === 'centre' && fitBox(ringOneBox(fresh.nodeId), 1, inStep)) return;
      // Zoom 1 at most, so a small branch is not blown up past the size Centre on focus shows.
      if (fresh?.kind === 'fit' && fit(fresh.nodeIds, 1, inStep)) return;
      const anchored = fresh?.kind === 'anchor' && anchoredViewport(getViewport(), before, positions, fresh.nodeIds);
      if (anchored) {
        void setViewport(anchored, inStep);
        return;
      }
      fit(null, store.getState().maxZoom, inStep);
    };
    if (moveMs <= 0) {
      reframe();
      return;
    }
    // The nodes' move starts its clock at the next animation frame (see `PositionMover.moveTo`); the view's
    // transition starts its own when it is set, so it is set in that same frame to stay in step. Until then the
    // view holds where it is, as the nodes do: a view move still running would shift an anchor off its point.
    void setViewport(getViewport());
    const frame = requestAnimationFrame(reframe);
    return () => cancelAnimationFrame(frame);
    // Runs on a new layout or a new request only: `positions`, `targets`, `edges`, and `moveMs` change with
    // `layoutKey`, and `mover` never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, request]);
  return null;
}
